import { defineComponent, PropType } from "vue";
import InputText from "../forms/InputText";
import Utils, { UserError } from "../utils/Utils";
import * as moment from "moment";
import { OfferCodeType } from "../utils/OfferUtils";
import { OfferJson } from "../OrderForm";

export default defineComponent({
    components: {
        InputText,
    },

    template: `
        <div class="CodeForm">
            <template v-if="!code">
                <button v-if="!edit" type="button" class="CodeForm__button" @click="edit = true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M19.54 5.35018C19.19 5.62018 19.12 6.12018 19.38 6.47018C20.59 8.07018 21.22 9.99018 21.22 12.0002C21.22 17.0802 17.08 21.2202 12 21.2202C6.91999 21.2202 2.77999 17.0802 2.77999 12.0002C2.77999 6.92018 6.91999 2.78018 12 2.78018C13.85 2.78018 15.63 3.32018 17.15 4.35018C17.52 4.60018 18.01 4.50018 18.26 4.14018C18.51 3.77018 18.41 3.28018 18.04 3.03018C16.25 1.82018 14.16 1.18018 11.99 1.18018C6.02999 1.18018 1.17999 6.03018 1.17999 12.0002C1.17999 17.9702 6.02999 22.8202 12 22.8202C17.97 22.8202 22.82 17.9702 22.82 12.0002C22.82 9.64018 22.07 7.39018 20.66 5.51018C20.4 5.16018 19.89 5.09018 19.54 5.35018Z" fill="currentColor"/>
                        <path d="M12 18.8099C12.44 18.8099 12.8 18.4499 12.8 18.0099V12.7999H18.01C18.45 12.7999 18.81 12.4399 18.81 11.9999C18.81 11.5599 18.45 11.1999 18.01 11.1999H12.8V5.98994C12.8 5.54994 12.44 5.18994 12 5.18994C11.56 5.18994 11.2 5.54994 11.2 5.98994V11.1999H5.99C5.55 11.1999 5.19 11.5599 5.19 11.9999C5.19 12.4399 5.55 12.7999 5.99 12.7999H11.2V18.0099C11.2 18.4499 11.56 18.8099 12 18.8099Z" fill="currentColor"/>
                    </svg>

                    Masz kod promocyjny?
                </button>

                <form v-if="edit" class="CodeForm__edit" @submit="onSubmit">
                    <InputText v-model="value" placeholder="Wprowadź kod" :error="error" :required="true" />

                    <button v-if="cancel_visible" type="button" class="CodeForm__cancel" @click="onCancel" :disabled="loading">Anuluj</button>
                    <button type="submit" :class="button_class" :disabled="loading">{{ button_label }}</button>
                </form>
            </template>

            <div v-if="code" class="CodeForm__code">
                <div class="CodeForm__code-label">Twój kod promocyjny</div>

                <div class="CodeForm__code-info">
                    <div class="CodeForm__code-value">{{ code.code }}</div>
                    <div v-if="code.validto" class="CodeForm__code-valid">Ważność: {{ validto }}</div>
                    <button type="button" class="CodeForm__code-cancel" @click="onCancel" :disabled="loading">Anuluj</button>
                </div>
            </div>
        </div>
    `,

    emits: [
        'editmode',
        'loading',
        'apply',
        'applyoptions',
        'cancel',
        'error',
    ],

    props: {
        type: { type: String as PropType<OfferCodeType>, require: true },
        sme: { type: Boolean, default: false },
        offer: { type: Object as PropType<OfferJson> },
        code: { type: Object },

        editmode: { type: Boolean, default: false },

        button_class: { type: String, default: "CodeForm__apply" },
        button_label: { type: String, default: "Zastosuj" },

        cancel_visible: { type: Boolean, default: true },
    },

    data(vm) {
        return {
            edit: this.editmode,

            value: null,
            error: null,

            loading: false,
        };
    },

    computed: {
        validto() {
            if (!this.code.validto) {
                return null;
            }

            return moment(this.code.validto).format('DD.MM.YYYY');
        },
    },

    watch: {
        edit() {
            this.$emit('editmode', this.edit);
        },
    },

    methods: {
        async onCancel(e: Event) {
            this.loading = true;

            this.$emit('loading');

            const r = await Utils.setCart(this.sme ? 'sme/code' : 'code', {
                type: this.type,
                code: null,
                offer: this.offer?.id,
            });

            this.error = null;
            this.value = null;
            this.edit = this.editmode;

            this.$emit('cancel');

            this.loading = false;
        },

        async onSubmit(e: Event) {
            e.preventDefault();

            try {
                this.loading = true;

                this.$emit('loading');

                const r = await Utils.setCart(this.sme ? 'sme/code' : 'code', {
                    type: this.type,
                    code: this.value,
                    offer: this.offer?.id,
                }, false);

                this.error = null;
                this.value = null;
                this.edit = this.editmode;

                this.$emit('applyoptions', r.options);
                this.$emit('apply', r.options.code);
            } catch (e) {
                if (e instanceof UserError) {
                    this.error = e.message;

                    this.$emit('error', this.value);
                }
            }

            this.loading = false;
        },
    },
});
